import { z } from 'zod';

export const newPasswordSchema = z
  .string()
  .min(10, 'Password must be at least 10 characters')
  .regex(/[A-Za-z]/, 'Must include a letter')
  .regex(/[0-9]/, 'Must include a number')
  .regex(/[^A-Za-z0-9]/, 'Must include a special character (e.g. !@#$%)');

// Login intentionally accepts any non-empty password — legacy users may have
// shorter passwords from prior systems. Strength is only enforced at registration
// and password reset.
export const loginSchema = z.object({
  email: z.email(),
  password: z.string().min(1, 'Password required'),
});

export const registerSchema = z
  .object({
    accountNum: z.string().min(1, 'Account number required'),
    firstName: z.string().min(1),
    lastName: z.string().min(1),
    email: z.email(),
    password: newPasswordSchema,
    passwordConfirm: z.string(),
    captchaToken: z.string().optional(),
  })
  .refine((d) => d.password === d.passwordConfirm, {
    path: ['passwordConfirm'],
    message: 'Passwords do not match',
  });

// Public /register is a lead-capture form, not self-service signup. We collect
// minimal contact info, email internal staff, and write nothing to auth/profiles.
export const dealerInquirySchema = z.object({
  accountNum: z.string().max(50).optional(),
  firstName: z.string().min(1, 'First name required').max(80),
  lastName: z.string().min(1, 'Last name required').max(80),
  companyName: z.string().min(1, 'Company name required').max(200),
  email: z.email(),
  phone: z.string().max(40).optional(),
  notes: z.string().max(2000).optional(),
  captchaToken: z.string().optional(),
});

export const forgotPasswordSchema = z.object({
  email: z.email(),
  captchaToken: z.string().optional(),
});

export type LoginInput = z.infer<typeof loginSchema>;
export type RegisterInput = z.infer<typeof registerSchema>;
export type DealerInquiryInput = z.infer<typeof dealerInquirySchema>;
